/**
 * Legacy staff order endpoints — wrappers used by Stage 1 drilldown pages.
 *
 *   - GET /staff/api/orders/                    list view (paginated)
 *   - GET /staff/api/orders/{id}/              detail view (timeline + items)
 *   - GET /staff/api/rider-logs/{order_id}/    optional rider playback
 *
 * The shapes below are deliberately permissive — the staff serializer is
 * generous (legacy + screenshot-able fields) and we want to remain
 * forward-compatible if a field gets renamed. The caller picks what it
 * needs and TypeScript widens unknown fields to `unknown`.
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { addDays } from 'date-fns';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { get } from '../client';
import { TIMEZONE } from '@/lib/format';
import type { TimeRangeMode } from '@/state/filters';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export type OrderStatus =
  | 'created'
  | 'packer_assigned'
  | 'packed'
  | 'billing'
  | 'picked'
  | 'reached'
  | 'completed'
  | 'cancelled'
  | string;

/**
 * One row in `OrderRow.batched_orders` — the list of orders that share a
 * trip/batch with the current row. The serializer
 * (`OrderTableSerializer.get_batched_orders` in `staff/serializers.py`)
 * returns one entry per leg of the trip including the row itself.
 */
export interface BatchedOrderEntry {
  order_id: number;
  status: OrderStatus | null;
  completed_timestamp: string | null;
  cancelled_timestamp: string | null;
  cancellation_reason: string | null;
  is_active: boolean;
  sequence: number | null;
}

export interface OrderRow {
  id: number;
  status: OrderStatus;
  total: number;
  final_total: number;
  total_before_discount?: number | string | null;
  coupon_discount?: number | string | null;
  coupon_code?: string | null;
  coupon_title?: string | null;
  quantity: number;
  sku_count: number;
  created_timestamp: string;
  packed_timestamp: string | null;
  billed_timestamp: string | null;
  picked_timestamp: string | null;
  reached_timestamp: string | null;
  completed_timestamp: string | null;
  cancelled_timestamp: string | null;
  cancellation_reason: string | null;
  customer_id: number | null;
  customer: string | null;
  store_id: number | string | null;
  store_name: string | null;
  rider_id: number | null;
  rider_name: string | null;
  rider_phone: string | null;
  uses_cycle: boolean;
  is_payment_done: boolean;
  payment_type: string | null;
  has_remarks: boolean;
  has_file: boolean;
  has_freeze_items: boolean;
  device_type: string | null;
  late_tags: string[] | Record<string, unknown> | null;
  return_timestamp: string | null;
  grouped_order_ids: number[];
  batched_orders: BatchedOrderEntry[];
  store_role: string | null;
  [k: string]: unknown;
}

export interface OrdersListResponse {
  next: string | null;
  previous: string | null;
  has_next_page: boolean;
  page: number;
  results: OrderRow[];
  total_items: number;
  filters?: Record<string, unknown>;
  range?: { start_iso: string | null; end_iso: string | null };
}

export interface OrdersListParams {
  from?: string;
  to?: string;
  search?: string;
  status?: string;
  storeId?: number | null;
  tags?: string;
  cancellationReason?: string | null;
  customerId?: number | null;
  riderId?: number | null;
  hasRemarks?: boolean | null;
  hasFile?: boolean | null;
  deviceType?: string | null;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  timeRangeMode?: TimeRangeMode;
  includeBiSlaTags?: boolean;
}

type DateBoundary = 'from' | 'to';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function normalizeOrderListDateParam(
  value: string,
  boundary: DateBoundary,
  timeRangeMode: TimeRangeMode = 'standard',
): string {
  if (!DATE_ONLY_RE.test(value)) return value;
  if (timeRangeMode === 'business') {
    if (boundary === 'from') {
      return toDate(`${value}T06:00:00.000`, { timeZone: TIMEZONE }).toISOString();
    }
    const dayAfter = formatInTimeZone(
      addDays(toDate(`${value}T00:00:00`, { timeZone: TIMEZONE }), 1),
      TIMEZONE,
      'yyyy-MM-dd',
    );
    return toDate(`${dayAfter}T05:59:59.999`, { timeZone: TIMEZONE }).toISOString();
  }
  const suffix = boundary === 'from' ? 'T00:00:00.000' : 'T23:59:59.999';
  return toDate(`${value}${suffix}`, { timeZone: TIMEZONE }).toISOString();
}

function ordersParams(p: OrdersListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: p.page ?? 1,
    page_size: p.pageSize ?? 25,
    sort_by: p.sortBy ?? 'created_timestamp',
    sort_order: p.sortOrder ?? 'desc',
  };
  if (p.from) out.from = normalizeOrderListDateParam(p.from, 'from', p.timeRangeMode);
  if (p.to) out.to = normalizeOrderListDateParam(p.to, 'to', p.timeRangeMode);
  if (p.search) out.search = p.search;
  if (p.status) out.status = p.status;
  if (p.storeId != null) out.store_id = p.storeId;
  if (p.tags) out.tags = p.tags;
  if (p.cancellationReason) out.cancellation_reason = p.cancellationReason;
  if (p.customerId != null) out.customer_id = p.customerId;
  if (p.riderId != null) out.rider_id = p.riderId;
  if (p.hasRemarks != null) out.has_remarks = p.hasRemarks ? 'true' : 'false';
  if (p.hasFile != null) out.has_file = p.hasFile ? 'true' : 'false';
  if (p.deviceType) out.device_type = p.deviceType;
  if (p.includeBiSlaTags) out.include_bi_sla_tags = '1';
  return out;
}

export function useOrdersList(p: OrdersListParams) {
  return useQuery({
    queryKey: [
      'legacy', 'orders-list',
      p.from, p.to, p.search ?? '', p.status ?? '', p.storeId ?? null,
      p.tags ?? '', p.cancellationReason ?? '', p.customerId ?? null, p.riderId ?? null,
      p.hasRemarks ?? null, p.hasFile ?? null, p.deviceType ?? null,
      p.page ?? 1, p.pageSize ?? 25,
      p.sortBy ?? 'created_timestamp', p.sortOrder ?? 'desc', p.timeRangeMode ?? 'standard',
      p.includeBiSlaTags ?? false,
    ],
    queryFn: () =>
      get<OrdersListResponse>('/staff/api/orders/', { params: ordersParams(p) }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

/**
 * One entry in a role's `remarks` array. The backend mixes two shapes
 * inside the same list:
 *
 *   1. Free-form text remarks added by support/staff:
 *      `{ id?, name, support?, at?, remarks: "..." }`
 *   2. Phone-call recordings persisted as `OrderRemarksFile`:
 *      `{ id, support?, name, phone, file, is_outgoing, at, called_at,
 *         duration, size }` — `file` is a Django MEDIA_URL path
 *      (e.g. `/media/order_remarks/123.m4a`).
 *
 * We surface both fields so the UI can render either a comment row or a
 * call row + audio player. See `OrderDetailAPIView.build_role_remarks_list`.
 */
export interface OrderRemark {
  id?: number | string | null;
  name?: string | null;
  phone?: string | null;
  remarks?: string | null;
  support?: { id: number; name: string | null } | null;
  /** Relative MEDIA path for call recordings (e.g. `/media/...m4a`). */
  file?: string | null;
  is_outgoing?: boolean;
  at?: string | null;
  called_at?: string | null;
  duration?: number | null;
  size?: number | null;
}

/**
 * Order detail payload from `/staff/api/orders/{id}/` (see
 * `staff/api_views.py::OrderDetailAPIView`).
 *
 * The serializer is hand-rolled and uses a nested shape (`order`,
 * `user_info`, `totals`, etc.) — this interface mirrors the actual
 * response so the BI page can pull the fields it needs without
 * defensive guess-work. Numeric money fields come back as JSON strings
 * (Decimal serialized with `str()`); we keep them as `string` and let
 * `fmt.npr` parse them.
 */
export interface OrderDetail {
  order: {
    id: number;
    status: OrderStatus;
    store?: string | null;
    distance?: number | string | null;
    rider_distance?: number | string | null;
    final_total: string;
    total_before_discount?: string | null;
    coupon_discount?: string | null;
    coupon_code?: string | null;
    coupon_title?: string | null;
    small_cart_fee: string;
    delivery_fee: string;
    created_timestamp: string | null;
    packed_timestamp: string | null;
    billed_timestamp: string | null;
    picked_timestamp: string | null;
    reached_timestamp: string | null;
    completed_timestamp: string | null;
    cancelled_timestamp: string | null;
    return_timestamp: string | null;
    cancellation_reason: string | null;
    is_first_order?: boolean;
    is_payment_done: boolean;
    payment_type: string;
    is_prepaid_online?: boolean;
  };
  store: {
    id: number;
    name: string;
    latitude: number | string | null;
    longitude: number | string | null;
  };
  user_info: {
    id: number | null;
    name: string | null;
    phone: string | null;
    address: string | null;
    latitude: number | string | null;
    longitude: number | string | null;
    remarks?: OrderRemark[];
  } | null;
  totals: {
    total_kg: string;
    total_l: string;
    total_cart_items: number;
    total_base_price: string;
    total_vat_amount: string;
    total_with_vat: string;
    base_small_cart_fee: string;
    base_delivery_fee: string;
    total_taxable_amount: string;
    vat_rate_percent: string;
    total_in_words: string;
  };
  order_items: Array<{
    id: number;
    product_availability_id?: number | null;
    product_id?: number | null;
    product_name?: string | null;
    quantity: number;
    size?: string | null;
    measurement?: string | null;
    price_with_vat: string;
    base_price: string;
    subtotal_base_price: string;
    vat_amount: string;
    total_with_vat: string;
  }>;
  edited_order_items: Array<{
    id: number,
    order_item_id?: number | null,
    is_on_order: boolean,
    product_availability_id: number,
    product_id: number,
    product_name: string,
    action: string,
    previous_quantity: number,
    new_quantity?: number | null,
    quantity_delta: number,
    unit_price: number,
    amount_delta: number,
    performed_by: {
      id: number,
      name: string,
      role: string
    },
    timestamp: string
  }>;
  packer: {
    id?: number | null;
    name?: string | null;
    phone?: string | null;
    remarks?: OrderRemark[];
  } | null;
  rider: {
    id?: number | null;
    name?: string | null;
    phone?: string | null;
    emergency_contact?: string | null;
    uses_cycle?: boolean | null;
    remarks?: OrderRemark[];
  } | null;
  biller: {
    id?: number | null;
    name?: string | null;
    phone?: string | null;
    remarks?: OrderRemark[];
  } | null;
  cancel_request?: {
    id: number;
    created_at: string | null;
    completed: boolean;
  } | null;
  bill?: string | null;
  seller_pan?: string | null;
  home_image?: string | null;
  payment_qr?: string | null;
  device?: Record<string, unknown> | null;
  late_tags?: Array<{ tag: string; late_duration?: number | null }> | null;
  has_freeze_items?: boolean;
  batched_orders?: Array<Record<string, unknown>>;
  [k: string]: unknown;
}

export function useOrderDetail(orderId: number | null) {
  return useQuery({
    queryKey: ['legacy', 'order-detail', orderId],
    queryFn: () =>
      get<OrderDetail>(`/staff/api/orders/${orderId}/`, {
        params: { include_bi_sla_tags: 1 },
      }),
    enabled: orderId != null && orderId > 0,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Optional: rider log breadcrumbs for the playback panel.
// ---------------------------------------------------------------------------

export interface RiderLogPoint {
  latitude: number;
  longitude: number;
  timestamp: string;
  speed?: number;
  bearing?: number;
}

export interface RiderLogsResponse {
  logs: RiderLogPoint[];
}

export function useRiderLogs(orderId: number | null) {
  return useQuery({
    queryKey: ['legacy', 'rider-logs', orderId],
    queryFn: () => get<RiderLogsResponse>(`/staff/api/rider-logs/${orderId}/`),
    enabled: orderId != null && orderId > 0,
    staleTime: 60_000,
    retry: 0,
  });
}
